import React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  BodyWrapper,
  Header,
  Loader,
  LocationForm,
  MainWrapper,
} from '@components';
import {
  constantsQuery,
  locationDeleteMutation,
  locationQuery,
  locationMutation,
  siteQuery,
  siteMutation,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';
import { DeleteLocation } from './DeleteLocation/DeleteLocation.jsx';
import { DisassociateLocation } from './DisassociateLocation/DisassociateLocation.jsx';

export const SiteLocationDetails = () => {
  const { t, i18n } = useTranslation();
  const { locationID, siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // current location from URL params or first location if no URL params
  const currentLocationID =
    locationID ||
    (siteData?.locations.length > 0 ? siteData?.locations[0].id : null);

  // get location
  const { data: locationData, isPending: locationIsPending } = useQuery(
    locationQuery(currentLocationID),
  );

  // get static data for fields
  const { data: constantsData, isPending: constantsIsPending } =
    useQuery(constantsQuery());

  // save location
  const mutation = useMutation(
    locationMutation(currentLocationID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['locations', 'list'] });
        // invalidate the detail for revisits
        queryClient.invalidateQueries({
          queryKey: ['locations', 'detail', currentLocationID],
        });
        // invalidate the site detail so the dropdown updates
        queryClient.invalidateQueries({
          queryKey: ['sites', 'detail', siteID],
        });
      },
    }),
  );
  const deleteLocation = useMutation(
    locationDeleteMutation(currentLocationID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['locations', 'list'] });
        // invalidate the detail for revisits
        queryClient.invalidateQueries({
          queryKey: ['sites', 'detail', siteID],
        });
        
        // scroll to the top of the page
        window.scrollTo(0, 0);
        navigate(`/dashboard/sites/${siteID}/location_details`);
      },
    }),
  );

  const updateSite = useMutation(
    siteMutation(siteID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['sites', 'list'] });
        // invalidate the detail for revisits
        queryClient.invalidateQueries({
          queryKey: ['sites', 'detail', siteID],
        });
        
        // scroll to the top of the page
        window.scrollTo(0, 0);
        navigate(`/dashboard/sites/${siteID}/location_details`);
      },
    }),
  );

  // shared isPending status
  const mutationPending =
    mutation.isPending || deleteLocation.isPending || updateSite.isPending;

  if (siteData && siteData.locations.length == 0) {
    // if the Site has no locations, redirect to the pages page
    return <Navigate to={`/dashboard/sites/${siteID}/pages`} replace />;
  }

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <span>{t('Location details')}</span>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              <span>{t('Basic information')}</span>
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            <div className="sb-manage-content-head sb-flex">
              <h1>{t('Basic information')}</h1>
              {siteData && siteData.locations.length > 1 && (
                <form
                  className="sb-manage-options-select"
                  key={currentLocationID}
                >
                  <label htmlFor="location-select">
                    {t('Select location')}
                  </label>
                  <select
                    defaultValue={currentLocationID}
                    onChange={(e) => {
                      navigate(
                        `/dashboard/sites/${siteID}/location_details/${e.target.value}`,
                      );
                    }}
                    id="location-select"
                  >
                    {siteData &&
                      siteData.locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.location_name || location.formatted_address}
                        </option>
                      ))}
                  </select>
                </form>
              )}
            </div>
            {(locationIsPending || constantsIsPending) && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {locationData && constantsData && (
              <>
                <LocationForm
                  key={currentLocationID} // force re-render when location changes from dropdown
                  constantsData={constantsData}
                  locationData={locationData}
                  mutation={mutation}
                  disabled={mutationPending}
                />
                <div className="sb-message-delete-section">
                  <DisassociateLocation
                    updateSite={updateSite}
                    siteData={siteData}
                    locationData={locationData}
                  />
                  <DeleteLocation
                    deleteLocation={deleteLocation}
                    locationData={locationData}
                  />
                </div>
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
